/**
 * Languages Meta accepts for a WhatsApp message template. Meta extends the list
 * over time, so a locale it doesn't cover yet resolves to no language.
 *
 * @see https://developers.facebook.com/docs/whatsapp/business-management-api/message-templates/supported-languages
 */
export const META_TEMPLATE_LANGUAGES = [
  "af",
  "sq",
  "ar",
  "ar_EG",
  "ar_AE",
  "ar_LB",
  "ar_MA",
  "ar_QA",
  "az",
  "be_BY",
  "bn",
  "bn_IN",
  "bg",
  "ca",
  "zh_CN",
  "zh_HK",
  "zh_TW",
  "hr",
  "cs",
  "da",
  "prs_AF",
  "nl",
  "nl_BE",
  "en",
  "en_GB",
  "en_US",
  "en_AE",
  "en_AU",
  "en_CA",
  "en_GH",
  "en_IE",
  "en_IN",
  "en_JM",
  "en_MY",
  "en_NZ",
  "en_QA",
  "en_SG",
  "en_UG",
  "en_ZA",
  "et",
  "fil",
  "fi",
  "fr",
  "fr_BE",
  "fr_CA",
  "fr_CH",
  "fr_CI",
  "fr_MA",
  "ka",
  "de",
  "de_AT",
  "de_CH",
  "el",
  "gu",
  "ha",
  "he",
  "hi",
  "hu",
  "id",
  "ga",
  "it",
  "ja",
  "kn",
  "kk",
  "rw_RW",
  "ko",
  "ky_KG",
  "lo",
  "lv",
  "lt",
  "mk",
  "ms",
  "ml",
  "mr",
  "nb",
  "ps_AF",
  "fa",
  "pl",
  "pt_BR",
  "pt_PT",
  "pa",
  "ro",
  "ru",
  "sr",
  "si_LK",
  "sk",
  "sl",
  "es",
  "es_AR",
  "es_CL",
  "es_CO",
  "es_CR",
  "es_DO",
  "es_EC",
  "es_HN",
  "es_MX",
  "es_PA",
  "es_PE",
  "es_ES",
  "es_UY",
  "sw",
  "sv",
  "ta",
  "te",
  "th",
  "tr",
  "uk",
  "ur",
  "uz",
  "vi",
  "zu",
] as const;

export type MetaTemplateLanguage = (typeof META_TEMPLATE_LANGUAGES)[number];

const META_TEMPLATE_LANGUAGE_SET: ReadonlySet<string> = new Set(
  META_TEMPLATE_LANGUAGES,
);

export function isMetaTemplateLanguage(
  value: string,
): value is MetaTemplateLanguage {
  return META_TEMPLATE_LANGUAGE_SET.has(value);
}

// Meta's code for a language it lists under another code, or for a region it lists no code for.
const META_LANGUAGE_FALLBACKS: ReadonlyMap<string, MetaTemplateLanguage> =
  new Map([
    ["no", "nb"],
    ["pt", "pt_PT"],
    ["zh", "zh_CN"],
    ["zh-MO", "zh_HK"],
    ["fa-AF", "prs_AF"],
  ]);

function parseLocale(locale: string): Intl.Locale | null {
  try {
    return new Intl.Locale(locale.trim().replaceAll("_", "-"));
  } catch {
    return null;
  }
}

/**
 * The language a template for this workspace locale is submitted in: Meta's
 * regional code when it has one (`fr-CA` as `fr_CA`), else the bare language
 * (`it-CH` as `it`), else null. Several locales can resolve to the same
 * language, so a template must not enable two of them.
 */
export function metaTemplateLanguageForLocale(
  locale: string,
): MetaTemplateLanguage | null {
  const parsed = parseLocale(locale);
  if (parsed === null) return null;
  const { language, region } = parsed;
  const candidates = [
    region && `${language}_${region}`,
    region && META_LANGUAGE_FALLBACKS.get(`${language}-${region}`),
    language,
    META_LANGUAGE_FALLBACKS.get(language),
  ];
  return (
    candidates.find(
      (candidate): candidate is MetaTemplateLanguage =>
        typeof candidate === "string" && isMetaTemplateLanguage(candidate),
    ) ?? null
  );
}
